import {
  paddingMap,
  themeBorderColorMap,
  themeHoverBackgroundMap,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import { type KeyboardEvent, forwardRef } from 'react'
import { getVirtualItemProps } from '../../hooks/use-virtualizer.tsx'
import { Checkbox } from '../input/checkbox.tsx'
import { Text } from '../text/text.tsx'
import type { MultiselectOptionProps } from './types.ts'
import { useMultiselectContext } from './use-multiselect.ts'
import { multiselectOptionHeight, optionPadding } from './utils.ts'

const optionHeight = multiselectOptionHeight()

export const MultiselectOption = forwardRef<
  HTMLDivElement,
  MultiselectOptionProps
>(({ item, virtualItem }, ref) => {
  const { onChange, selectedItems } = useMultiselectContext()

  const handleClick = () => {
    const selected = selectedItems.some(
      (selectedItem) => selectedItem.id === item.id
    )

    if (selected) {
      onChange(
        selectedItems.filter((selectedItem) => item.id !== selectedItem.id)
      )
    } else {
      onChange([...selectedItems, item])
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Space') {
      e.preventDefault()
      e.stopPropagation()

      handleClick()
    }
  }

  const isVirtualized = !!virtualItem

  return (
    <div
      className={cn(
        paddingMap[optionPadding],
        'flex justify-start items-center gap-2.5 cursor-pointer border-b w-full overflow-hidden',
        themeBorderColorMap.muted,
        themeHoverBackgroundMap.tertiary
      )}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      ref={ref}
      style={
        isVirtualized
          ? {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: `${optionHeight}px`,
              transform: `translateY(${virtualItem?.start}px)`,
            }
          : undefined
      }
      {...(isVirtualized ? getVirtualItemProps(virtualItem) : {})}
    >
      <Checkbox
        value={
          !!selectedItems?.find((selectedItem) => selectedItem.id === item.id)
        }
        className="pointer-events-none"
        onChange={handleClick}
      >
        <Text className="flex-initial truncate w-full">{item.name}</Text>
      </Checkbox>
    </div>
  )
})
